import React, {ReactElement} from 'react';
import {FormattedDate} from 'react-intl';
import {cx} from '@emotion/css';
import {GatsbyImage} from 'gatsby-plugin-image';

import SocialShare from '@fo/designSystem/SocialShare/SocialShare';
import {fonts} from '@fo/shared/styles/variables';
import Headers, {HeaderType} from '@fo/designSystem/Text/Headers/Headers';
import Paragraph from '@fo/designSystem/Text/Paragraph/Paragraph';
import {Hero} from '@fo/shared-types';
import {isBrowser} from '@fo/shared/utils/browserSupport';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';

import styles from './BlogArticleHero.styles';

interface Props extends Hero {
  title: string;
  category: string;
  createdAt: string | number | Date;
  description: string;
  categorySlug: string;
}

function BlogArticleHero({
  category,
  categorySlug,
  createdAt,
  description,
  title,
  image,
  source,
}: Readonly<Props>): ReactElement {
  return (
    <div className={cx(styles.blogArticleHero)}>
      <div className={cx(styles.titleContainer)}>
        <DynamicLink href={categorySlug} className={cx(styles.categoryName, 'unstyledLink')}>
          <Paragraph fontSize={fonts.size.L} fontWeight={fonts.weight.REGULAR} alignment={fonts.alignment.CENTER}>
            {category}
          </Paragraph>
        </DynamicLink>
        <Headers headerType={HeaderType.H1} alignment={fonts.alignment.CENTER}>
          {title}
        </Headers>
        <Paragraph
          fontWeight={fonts.weight.REGULAR}
          fontSize={fonts.size.S}
          alignment={fonts.alignment.CENTER}
          className={cx(styles.publicationDate)}
        >
          <FormattedDate value={createdAt} day="numeric" month="short" year="numeric" />
        </Paragraph>
        <Paragraph
          fontWeight={fonts.weight.REGULAR}
          fontSize={fonts.size.L}
          alignment={fonts.alignment.CENTER}
          className={cx(styles.shortDescription)}
        >
          {description}
        </Paragraph>
        <SocialShare urlToShare={isBrowser ? window.location.href : ''} />
      </div>
      {image && (
        <div className={cx(styles.imageContainer)}>
          <GatsbyImage
            className={cx(styles.heroImage)}
            loading="lazy"
            image={image.gatsbyImageData}
            alt={image.title}
          />
          {source && <span className={cx(styles.heroImageSource)}>{source}</span>}
        </div>
      )}
    </div>
  );
}

export default BlogArticleHero;
